import axiosInstance from "../../api/axiosInstance";
import { Table, BasicTable, StartOrderPayload, OrderItem } from "./types";

// API Endpoint: GET /api/getAllTablesDetails
// Payload: { restaurantGuid: string }
// Response: { data: BasicTable[] }
// Example Payload: { "restaurantGuid": "123e4567-e89b-12d3-a456-426614174000" }
// Example Response: { "data": [{ "tableId": "T001", "name": "Table 1", "isAvailable": true }] }
export const getAllTables = async (restaurantGuid: string): Promise<BasicTable[]> => {
  const res = await axiosInstance.get(`/table/GetTableSessionDetails`);
  return res?.data ?? [];
};

// API Endpoint: POST /api/getTableDetails
// Payload: { tableId: string, restaurantGuid: string }
// Response: { data: Table }
// Example Payload: { "tableId": "T001", "restaurantGuid": "123e4567-e89b-12d3-a456-426614174000" }
// Example Response: { "data": { "tableId": "T001", "name": "Table 1", "isAvailable": false, "currentOrder": { ... } } }
export const getTableDetails = async (tableId: string, restaurantGuid: string): Promise<Table> => {
  const res = await axiosInstance.post(`/getTableDetails`, { tableId, restaurantGuid });
  return res?.data?.data;
};

// API Endpoint: POST /api/startOrder
// Payload: StartOrderPayload
// Response: { data: Table }
// Example Payload: { "tableId": "T001", "customerCount": 4, "waiterName": "John Doe", "customerName": "Jane Smith", "customerEmail": "jane@example.com", "customerMobile": "1234567890", "restaurantGuid": "123e4567-e89b-12d3-a456-426614174000" }
// Example Response: { "data": { "tableId": "T001", "name": "Table 1", "isAvailable": false, "currentOrder": { ... } } }
export const startOrder = async (payload: StartOrderPayload): Promise<Table> => {
  const res = await axiosInstance.post(`/startOrder`, payload);
  return res?.data?.data;
};

// API Endpoint: POST /api/addItemToOrder
// Payload: { tableId: string, item: OrderItem, restaurantGuid: string }
// Response: { data: Table }
// Example Payload: { "tableId": "T001", "item": { "id": "I001", "name": "Pizza", "price": 10.99, "qty": 2 }, "restaurantGuid": "123e4567-e89b-12d3-a456-426614174000" }
// Example Response: { "data": { "tableId": "T001", ... } }
export const addItemToOrder = async (tableId: string, item: OrderItem, restaurantGuid: string) => {
  const res = await axiosInstance.post(`/addItemToOrder`, { tableId, item, restaurantGuid });
  return res?.data?.data;
};

// API Endpoint: POST /api/updateItemQty
// Payload: { tableId: string, itemId: string, qty: number, restaurantGuid: string }
// Response: { data: Table }
// Example Payload: { "tableId": "T001", "itemId": "I001", "qty": 3, "restaurantGuid": "123e4567-e89b-12d3-a456-426614174000" }
// Example Response: { "data": { "tableId": "T001", ... } }
export const updateItemQty = async (tableId: string, itemId: string, qty: number, restaurantGuid: string) => {
  const res = await axiosInstance.post(`/updateItemQty`, { tableId, itemId, qty, restaurantGuid });
  return res?.data?.data;
};

// API Endpoint: POST /api/removeItem
// Payload: { tableId: string, itemId: string, restaurantGuid: string }
// Response: { data: Table }
// Example Payload: { "tableId": "T001", "itemId": "I001", "restaurantGuid": "123e4567-e89b-12d3-a456-426614174000" }
// Example Response: { "data": { "tableId": "T001", ... } }
export const removeItem = async (tableId: string, itemId: string, restaurantGuid: string) => {
  const res = await axiosInstance.post(`/removeItem`, { tableId, itemId, restaurantGuid });
  return res?.data?.data;
};

// API Endpoint: POST /api/finalizeBill
// Payload: { tableId: string, restaurantGuid: string }
// Response: { data: any }
// Example Payload: { "tableId": "T001", "restaurantGuid": "123e4567-e89b-12d3-a456-426614174000" }
// Example Response: { "data": { "billId": "B001", ... } }
export const finalizeBill = async (tableId: string, restaurantGuid: string) => {
  const res = await axiosInstance.post(`/finalizeBill`, { tableId, restaurantGuid });
  return res?.data?.data;
};

// API Endpoint: POST /api/moveTable
// Payload: { fromTableId: string, toTableId: string, restaurantGuid: string }
// Response: { data: any }
// Example Payload: { "fromTableId": "T001", "toTableId": "T002", "restaurantGuid": "123e4567-e89b-12d3-a456-426614174000" }
// Example Response: { "data": "Order moved successfully" }
export const moveTable = async (fromTableId: string, toTableId: string, restaurantGuid: string) => {
  const res = await axiosInstance.post(`/moveTable`, { fromTableId, toTableId, restaurantGuid });
  return res?.data?.data;
};

// API Endpoint: POST /api/mergeTables
// Payload: { sourceTableId: string, targetTableId: string, restaurantGuid: string }
// Response: { data: any }
// Example Payload: { "sourceTableId": "T001", "targetTableId": "T002", "restaurantGuid": "123e4567-e89b-12d3-a456-426614174000" }
// Example Response: { "data": "Tables merged successfully" }
export const mergeTables = async (sourceTableId: string, targetTableId: string, restaurantGuid: string) => {
  const res = await axiosInstance.post(`/mergeTables`, { sourceTableId, targetTableId, restaurantGuid });
  return res?.data?.data;
};

// API Endpoint: POST /api/printKOT
// Payload: { tableId: string, restaurantGuid: string }
// Response: { data: any }
// Example Payload: { "tableId": "T001", "restaurantGuid": "123e4567-e89b-12d3-a456-426614174000" }
// Example Response: { "data": "KOT printed successfully" }
export const printKOT = async (tableId: string, restaurantGuid: string) => {
  const res = await axiosInstance.post(`/printKOT`, { tableId, restaurantGuid });
  return res?.data?.data;
};
